import { oldKeys } from "./keyring";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, type Listing, type MessageRow } from "./api";
import { describe, hidePrivate, mergeCopies, mergeGroups, type CapsuleView, type DescribeContext } from "./capsules";
import { errorText } from "./i18n";
import { useSession } from "./session";
import { useRefreshHandler } from "./Refresh";
import { keepOpened, type ListQuery } from "./messageFilters";

/** What every paged view carries: an id, and the Lock count it was made under. */
export type PagedView = { id: string; gen?: number };

/**
 * One list, one page at a time (PLAN.md 4.20): the server chooses, filters and orders the rows; the
 * browser describes (unwraps) only the pages asked for. A new query starts over at the first page;
 * "Show more" appends the next one. Never opens a time lock or asks for a key. The message lists
 * (`usePagedMessages`) and the Correspondence lists (`useEnvelopes`, PLAN.md 4.21) share this core.
 */
export function usePagedViews<R extends MessageRow, V extends PagedView>(args: {
  query: ListQuery | null;
  /** Rows of one page into views, each stamped with the current Lock count. */
  describePage: (rows: R[]) => Promise<V[]>;
  /** A view made under an earlier Lock count, with what came from the key hidden. */
  hide: (view: V) => V;
  /** Refresh: which of the old views to keep (content opened under the same Lock count). */
  keep: (previous: V[], fresh: V[], locks: number) => V[];
}) {
  const { query, describePage, hide, keep } = args;
  const { keyGeneration, keyStillValid, lockGeneration } = useSession();
  const key = query ? `${query.path}?${new URLSearchParams(query.params)}` : "";
  const [views, setViews] = useState<V[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = useRef<AbortController | null>(null);
  // A reload in flight: "Show more" waits, or its page would land on the first page the reload
  // brings and the pages between would be skipped (4.21 review, finding 7).
  const reloading = useRef(false);

  /** The first page. `quiet` (Refresh, a delivery): rows stay on screen until it arrives, and keep content opened under this Lock. */
  const reload = useCallback(async (quiet = false) => {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    reloading.current = true;
    const generation = keyGeneration, locks = lockGeneration;
    const current = () => !controller.signal.aborted && keyStillValid(generation);
    setLoadingMore(false);
    if (!query) { setViews([]); setNext(null); setLoading(false); return; }
    if (!quiet) { setViews([]); setNext(null); setLoading(true); }
    setError(null);
    try {
      const page = await api.list<R>(query, undefined, controller.signal);
      if (!current()) return;
      const fresh = await describePage(page.messages);
      if (!current()) return;
      setViews((previous) => (quiet ? keep(previous, fresh, locks) : fresh));
      setNext(page.next_cursor);
    } catch (err) {
      if (!current()) return;
      setError(errorText(err));
      if (quiet) throw err;
    } finally { if (active.current === controller) reloading.current = false; if (current()) setLoading(false); }
  // `key` stands for the query's content; the object itself is rebuilt on every render.
  }, [key, describePage, keep, keyGeneration, keyStillValid, lockGeneration]);

  const loadMore = useCallback(async () => {
    if (!query || !next || loadingMore || reloading.current) return;
    const controller = new AbortController();
    active.current = controller;
    const generation = keyGeneration;
    const current = () => !controller.signal.aborted && keyStillValid(generation);
    setLoadingMore(true);
    setError(null);
    try {
      const page = await api.list<R>(query, next, controller.signal);
      if (!current()) return;
      const more = await describePage(page.messages);
      if (!current()) return;
      setViews((previous) => { const seen = new Set(previous.map((v) => v.id)); return [...previous, ...more.filter((v) => !seen.has(v.id))]; });
      setNext(page.next_cursor);
    } catch (err) {
      if (current()) setError(errorText(err));
    } finally { if (current()) setLoadingMore(false); }
  }, [key, next, loadingMore, describePage, keyGeneration, keyStillValid]);

  useEffect(() => { void reload(); return () => active.current?.abort(); }, [reload]);
  useRefreshHandler(() => reload(true));
  const shown = useMemo(() => views.map((view) => (view.gen === lockGeneration ? view : hide(view))), [views, lockGeneration, hide]);
  /** A view that left this list (archived or restored): gone without reloading the pages. */
  const removeView = (id: string) => setViews((list) => list.filter((view) => view.id !== id));
  return { views: shown, setViews, loading, loadingMore, hasMore: next !== null, error, reload, loadMore, removeView };
}

/** The describe context of this session: every key the member ever had, to recognise capsules to an earlier key (4.16 K4). */
export function useDescribeContext(listing: Listing): { ctx: Omit<DescribeContext, "oldKeys">; withOldKeys: () => Promise<DescribeContext> } {
  const { me, secretKey, keyStatus, myKeys } = useSession();
  const ctx = useMemo<Omit<DescribeContext, "oldKeys">>(() => ({ listing, memberId: me.id, myPubkey: me.pubkey, secretKey, keyLocked: keyStatus === "locked", hideSenders: me.settings.hide_senders, myKeys }), [listing, me.id, me.pubkey, me.settings.hide_senders, myKeys, secretKey, keyStatus]);
  const withOldKeys = useCallback(async () => ({ ...ctx, oldKeys: secretKey ? await oldKeys(secretKey) : undefined }), [ctx, secretKey]);
  return { ctx, withOldKeys };
}

/** Feed, Inbox and Sent, one page at a time; Sent pairs the author's copy with its wrap and shows a group as one row. */
export function usePagedMessages(listing: "feed" | "inbox" | "sent", query: ListQuery | null) {
  const { lockGeneration, resolveMembers } = useSession();
  const { withOldKeys } = useDescribeContext(listing);

  const describePage = useCallback(async (rows: MessageRow[]) => {
    const ctx = await withOldKeys();
    // Sent lists the author's copy with its wrap; mergeCopies pairs them, within the page.
    const flat = rows.flatMap((row) => (row.copy ? [row, row.copy] : [row]));
    const described = await Promise.all(flat.map((row) => describe(row, ctx)));
    // The names these rows show (PLAN.md 4.20 step 5): authors and recipients the page does not know yet.
    resolveMembers({ keys: described.flatMap((view) => [view.author, view.recipient, ...(view.recipients ?? [])]).filter((key): key is string => !!key) });
    return mergeGroups(mergeCopies(described)).map((view) => ({ ...view, gen: lockGeneration }));
  }, [withOldKeys, lockGeneration, resolveMembers]);

  const paged = usePagedViews<MessageRow, CapsuleView>({ query, describePage, hide: hidePrivate, keep: keepOpened });
  const updateView = (changed: CapsuleView) => paged.setViews((list) => list.map((view) => (view.id === changed.id ? { ...changed, gen: lockGeneration } : view)));
  const { setViews: _setViews, ...rest } = paged;
  return { ...rest, updateView, describePage };
}
